import styled from '@emotion/styled';
import {
    BodyTextSmall,
    ComponentTextMedium,
    ComponentTextSmall,
    Icon,
    pxToRem,
    Tooltip,
    useViewport,
    HeaderNavigationItem,
    HeaderNavigationButton,
    Badge,
    Menu as DropdownMenu,
} from '@modernhealth/vital-components';
import { colors } from '@modernhealth/vital-foundation';
import { EventLexicon } from '@modernhealth/analytics-lexicon';
import { useAnalytics } from '@modernhealth/web-analytics';
import { MenuButton as MB, Menu, MenuItem as MI, MenuList as ML } from '@reach/menu-button';
import { captureException } from 'src/shared/helpers/captureException';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { ReactElement } from 'react-markdown';

import { navigationService } from 'src/config/rootService';
import { HrAdminPermission } from 'src/generated/webapp_gql';
import { config } from 'src/modules/admin/config/config';
import { getFirstName, getFullName } from 'src/modules/user/selectors/userSelectors';
import { ReactComponent as OpenBookIcon } from 'src/public/img/icon-open-book.svg';
import { ReactComponent as UserIcon } from 'src/public/img/icon-user-blue.svg';
import { ReactComponent as ToolIcon } from 'src/public/img/icon-wrench-tool.svg';
import * as S from 'src/shared/layout/styles';

import { myHrAdminRolePermissionsSelector } from '../selectors/adminSelectors';
import { AdminResourcesDropdown } from '../resources/components/AdminResourcesDropdown';

const MenuButton = styled(MB)(({ disabled = false }: { disabled?: boolean }) => ({
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    padding: pxToRem(16),
    border: 'none',
    position: 'relative',
    top: 0,
    left: 0,
    backgroundColor: 'inherit',
    '&:hover': {
        cursor: 'pointer',
        borderRadius: pxToRem(8),
    },
    ...(disabled && {
        color: colors.gray85,
        opacity: '0.3',
        '&:hover': {
            cursor: 'not-allowed',
        },
    }),
}));

const MenuButtonLabel = styled(ComponentTextMedium)({
    margin: pxToRem(0, 8),
});

const MenuList = styled(ML)({
    display: 'flex',
    flexDirection: 'column',
    minWidth: pxToRem(308),
    border: 'none',
    borderRadius: pxToRem(12),
    padding: pxToRem(16),
    backgroundColor: colors.white,
    boxShadow: '0px 0px 8px rgba(33, 38, 46, 0.12), 0px 4px 12px rgba(33, 38, 46, 0.16)',
    position: 'absolute',
});

interface MenuItemProps {
    disabled?: boolean;
}
const MenuItem = styled(MI)(({ disabled = false }: MenuItemProps) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'start',
    alignItems: 'flex-start',
    gap: pxToRem(4),
    padding: pxToRem(16),
    height: '100%',
    userSelect: 'text',
    '&:hover': {
        backgroundColor: disabled ? colors.white : colors.blue05,
        cursor: disabled ? 'not-allowed' : 'pointer',
        borderRadius: pxToRem(8),
        userSelect: 'text',
    },
    opacity: disabled ? '0.3' : 1,
}));

const Label = styled(ComponentTextMedium)({
    borderRadius: pxToRem(12),
});

const Description = styled(BodyTextSmall)({
    borderRadius: pxToRem(12),
    padding: pxToRem(4, 0),
});

const AdminBadge = styled(ComponentTextSmall)({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: pxToRem(5),
    width: pxToRem(104),
    maxHeight: pxToRem(21),
    backgroundColor: colors.blue10,
    textAlign: 'center',
    color: colors.blue55,
    margin: pxToRem(0, 16),
    padding: pxToRem(4, 0),
    '&:hover': {
        color: colors.blue55,
    },
});

const Wrapper = styled.div({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
});

const StyledUserIcon = styled(UserIcon)({
    color: colors.blue55,
});

const StyledOpenBookIcon = styled(OpenBookIcon)(({ disabled = false }: { disabled?: boolean }) => ({
    color: disabled ? colors.gray85 : colors.blue55,
}));

const StyledToolIcon = styled(ToolIcon)({
    color: colors.blue55,
});

const AccountUser = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: ${pxToRem(28, 16, 16, 28)};
`;
const AccountUserName = styled.div`
    padding-right: ${pxToRem(28)};
`;

export interface ConditionalPermissionsTooltipProps {
    disabled: boolean;
    children: string;
    trigger: ReactElement;
}

export const ConditionalPermissionsTooltip: React.FC<ConditionalPermissionsTooltipProps> = ({
    disabled,
    children,
    trigger,
}) => {
    return <>{!disabled ? <> {trigger} </> : <Tooltip trigger={trigger}>{children}</Tooltip>}</>;
};

interface Props {
    onLogOut: () => void;
}

export const AdminProfileMenu: React.FC<Props> = ({ onLogOut }) => {
    const { t } = useTranslation();
    const fullName = useSelector(getFullName);
    const firstName = useSelector(getFirstName);
    const { VIEWPORT_LG_MAX, VIEWPORT_LG_MIN } = useViewport();
    const { adminPortalPermissionsV1Enabled, enableGlobalNavigationFeature } = useFlags();
    const myHrAdminRolePermissions = useSelector(myHrAdminRolePermissionsSelector);
    const canManageAccounts = myHrAdminRolePermissions.includes(HrAdminPermission.AccountsManage);
    let title = '';
    if (adminPortalPermissionsV1Enabled && canManageAccounts) {
        title = t('admin:header.admin_badge.title');
    } else if (!adminPortalPermissionsV1Enabled) {
        title = t('admin:admin_view');
    }

    /**
     * Global Navigation Feature
     */
    if (enableGlobalNavigationFeature) {
        const menuItems = [
            {
                id: 'switch',
                title: t('admin:switch_to_member_view'),
                onAction: () => navigationService.navigate('/dashboard'),
            },
            {
                id: 'logout',
                title: t('shared:header.log_out'),
                onAction: onLogOut,
            },
        ];

        return (
            <>
                <HeaderNavigationItem stacked={VIEWPORT_LG_MAX}>
                    <DropdownMenu
                        placement="bottom left"
                        trigger={
                            <HeaderNavigationButton
                                icon="person"
                                text={VIEWPORT_LG_MIN ? fullName : 'Profile'}
                                hasMenu={VIEWPORT_LG_MIN}
                            />
                        }
                        items={menuItems}
                        startEnhancer={
                            <AccountUser>
                                <AccountUserName>
                                    <ComponentTextMedium fontColor="gray85">
                                        {firstName}
                                    </ComponentTextMedium>
                                </AccountUserName>
                                <Badge variant="info" title="Super admin" />
                            </AccountUser>
                        }
                    />
                </HeaderNavigationItem>
            </>
        );
    }

    return (
        <Wrapper>
            {title && <AdminBadge>{title}</AdminBadge>}
            <Menu>
                <MenuButton>
                    <StyledUserIcon />
                    <S.UserName>{fullName}</S.UserName>
                    <Icon name="triangle-down" />
                </MenuButton>
                <MenuList>
                    <MenuItem
                        onSelect={() => {
                            navigationService.navigate('/dashboard');
                        }}
                    >
                        <Description bold>{t('admin:switch_to_member_view')}</Description>
                    </MenuItem>
                    <MenuItem onSelect={() => onLogOut()}>
                        <Description bold>{t('shared:header.log_out')}</Description>
                    </MenuItem>
                </MenuList>
            </Menu>
        </Wrapper>
    );
};

export const AdminResourcesMenu: FC = () => {
    const { t } = useTranslation();
    const analytics = useAnalytics();
    const history = useHistory();
    const { VIEWPORT_LG_MIN, VIEWPORT_LG_MAX } = useViewport();
    const {
        clientPortalUnification,
        adminPortalPermissionsV1Enabled,
        enableGlobalNavigationFeature,
    } = useFlags();
    const myHrAdminRolePermissions = useSelector(myHrAdminRolePermissionsSelector);
    const canSeeResources =
        !adminPortalPermissionsV1Enabled ||
        myHrAdminRolePermissions.includes(HrAdminPermission.ResourceView);

    const copyToClipboard = async () => {
        try {
            await navigator.clipboard.writeText(config.clientPortalPassword);
        } catch (err) {
            captureException(err);
        }
    };

    const handleOpenClientPortal = async () => {
        if (clientPortalUnification) {
            history.push('/admin/resources');
            return;
        }

        await copyToClipboard();
        analytics.trackEvent(
            EventLexicon.clickAdminPortalNavigationBar({
                translationKey: 'admin:menu.resources.button.title',
                destination: config.clientPortalUrl,
                pageSection: 'navigation bar',
            }),
        );
        window.open(config.clientPortalUrl, '_blank');
    };

    if (enableGlobalNavigationFeature) {
        const items = [
            {
                id: 'client-portal',
                title: t('admin:menu.resources.menu_item.engagement_resources.label'),
                description: t('admin:menu.resources.menu_item.engagement_resources.description', {
                    password: config.clientPortalPassword,
                }),
                onAction: handleOpenClientPortal,
            },
        ];
        const resourcesButton = (
            <HeaderNavigationButton
                icon="document"
                text={t('admin:menu.resources.button.title')}
                disabled={!canSeeResources}
                hasMenu={VIEWPORT_LG_MIN}
            />
        );

        if (clientPortalUnification) {
            return <AdminResourcesDropdown />;
        } else {
            return (
                <HeaderNavigationItem stacked={VIEWPORT_LG_MAX}>
                    {canSeeResources ? (
                        <DropdownMenu
                            trigger={resourcesButton}
                            items={items}
                            placement="bottom left"
                        />
                    ) : (
                        <Tooltip trigger={resourcesButton}>
                            {t('admin:admin_permissions_tooltip.hover.insufficient_permissions')}
                        </Tooltip>
                    )}
                </HeaderNavigationItem>
            );
        }
    }

    return (
        <Wrapper>
            <Menu>
                <ConditionalPermissionsTooltip
                    trigger={
                        <MenuButton disabled={!canSeeResources}>
                            <StyledOpenBookIcon disabled={!canSeeResources} />
                            <MenuButtonLabel>
                                {t('admin:menu.resources.button.title')}
                            </MenuButtonLabel>
                            <Icon name="triangle-down" />
                        </MenuButton>
                    }
                    disabled={!canSeeResources}
                >
                    {t('admin:admin_permissions_tooltip.hover.insufficient_permissions')}
                </ConditionalPermissionsTooltip>

                {clientPortalUnification ? (
                    <AdminResourcesDropdown />
                ) : (
                    <MenuList>
                        <MenuItem onSelect={handleOpenClientPortal}>
                            <Label bold>
                                {t('admin:menu.resources.menu_item.engagement_resources.label')}
                            </Label>
                            <Description css={{ width: '90%', padding: 0 }}>
                                {t(
                                    'admin:menu.resources.menu_item.engagement_resources.description',
                                    {
                                        password: config.clientPortalPassword,
                                    },
                                )}
                            </Description>
                            <Description css={{ width: '100%', padding: 0 }}>
                                {t('admin:menu.resources.menu_item.engagement_resources.hint')}
                            </Description>
                        </MenuItem>
                    </MenuList>
                )}
            </Menu>
        </Wrapper>
    );
};

export const AdminToolsMenu: React.FC = () => {
    const { t } = useTranslation();
    const analytics = useAnalytics();
    const { pathname } = useLocation();
    const { VIEWPORT_LG_MIN, VIEWPORT_LG_MAX } = useViewport();
    const {
        showHrisConnectButton,
        adminPortalPermissionsV1Enabled,
        enableGlobalNavigationFeature,
    } = useFlags();
    const myHrAdminRolePermissions = useSelector(myHrAdminRolePermissionsSelector);
    const canSeeAccounts =
        !adminPortalPermissionsV1Enabled ||
        myHrAdminRolePermissions.includes(HrAdminPermission.AccountsView);
    const canSeeBenefits =
        !adminPortalPermissionsV1Enabled ||
        myHrAdminRolePermissions.includes(HrAdminPermission.BenefitsView);
    const canSeeHris =
        !adminPortalPermissionsV1Enabled ||
        myHrAdminRolePermissions.includes(HrAdminPermission.HrisView);

    const handleOnClick = (path: string, key: string) => {
        analytics.trackEvent(
            EventLexicon.clickAdminPortalNavigationBar({
                translationKey: key,
                destination: path,
                pageSection: 'navigation bar',
            }),
        );
        navigationService.navigate(path);
    };

    if (enableGlobalNavigationFeature) {
        const items = [
            {
                id: 'manage-benefits',
                title: t('admin:menu.tools.menu_item.manage_benefits.label'),
                description: t('admin:menu.tools.menu_item.manage_benefits.description'),
                onAction: () =>
                    handleOnClick(
                        '/admin/benefits',
                        'admin:menu.tools.menu_item.manage_benefits.label',
                    ),

                /**
                 * Extending item properties
                 */
                disabled: !canSeeBenefits,
                active: pathname === '/admin/benefits',
            },
            {
                id: 'manage-admin',
                title: adminPortalPermissionsV1Enabled
                    ? t('admin:menu.tools.menu_item.manage_admin.label_v1')
                    : t('admin:menu.tools.menu_item.manage_admin.label'),
                description: adminPortalPermissionsV1Enabled
                    ? t('admin:menu.tools.menu_item.manage_admin.description_v1')
                    : t('admin:menu.tools.menu_item.manage_admin.description'),
                onAction: () =>
                    handleOnClick('/admin/manage', 'admin:menu.tools.menu_item.manage_admin.label'),

                /**
                 * Extending item properties
                 */
                disabled: !canSeeAccounts,
                active: pathname === '/admin/manage',
            },
        ];

        if (showHrisConnectButton) {
            items.push({
                id: 'hris-sync',
                title: t('admin:menu.tools.menu_item.hris_sync.label'),
                description: t('admin:menu.tools.menu_item.hris_sync.description'),
                onAction: () =>
                    handleOnClick('/admin/hris-sync', 'admin:menu.tools.menu_item.hris_sync.label'),

                /**
                 * Extending item properties
                 */
                disabled: !canSeeHris,
                active: pathname === '/admin/hris-sync',
            });
        }

        const disabledIds = items.filter(item => item.disabled).map(item => item.id) as string[];
        const selectedIds = items.filter(item => item.active).map(item => item.id) as string[];

        return (
            <HeaderNavigationItem stacked={VIEWPORT_LG_MAX}>
                <DropdownMenu
                    trigger={
                        <HeaderNavigationButton
                            icon="tools"
                            text={t('admin:menu.tools.button.title')}
                            hasMenu={VIEWPORT_LG_MIN}
                        />
                    }
                    items={items}
                    disabledIds={disabledIds}
                    selectedIds={selectedIds}
                    placement="bottom left"
                />
            </HeaderNavigationItem>
        );
    }

    return (
        <Wrapper>
            <Menu>
                <MenuButton>
                    <StyledToolIcon color={colors.blue55} aria-label="tools icon" />
                    <MenuButtonLabel>{t('admin:menu.tools.button.title')}</MenuButtonLabel>
                    <Icon name="triangle-down" />
                </MenuButton>
                <MenuList>
                    <ConditionalPermissionsTooltip
                        trigger={
                            <MenuItem
                                onSelect={() => {
                                    handleOnClick(
                                        '/admin/benefits',
                                        'admin:menu.tools.menu_item.manage_benefits.label',
                                    );
                                }}
                                disabled={!canSeeBenefits}
                            >
                                <Label bold>
                                    {t('admin:menu.tools.menu_item.manage_benefits.label')}
                                </Label>
                                <Description>
                                    {t('admin:menu.tools.menu_item.manage_benefits.description')}
                                </Description>
                            </MenuItem>
                        }
                        disabled={!canSeeBenefits}
                    >
                        {t('admin:admin_permissions_tooltip.hover.insufficient_permissions')}
                    </ConditionalPermissionsTooltip>

                    <ConditionalPermissionsTooltip
                        trigger={
                            <MenuItem
                                onSelect={() => {
                                    handleOnClick(
                                        '/admin/manage',
                                        'admin:menu.tools.menu_item.manage_admin.label',
                                    );
                                }}
                                disabled={!canSeeAccounts}
                            >
                                <Label bold>
                                    {adminPortalPermissionsV1Enabled
                                        ? t('admin:menu.tools.menu_item.manage_admin.label_v1')
                                        : t('admin:menu.tools.menu_item.manage_admin.label')}
                                </Label>
                                <Description>
                                    {adminPortalPermissionsV1Enabled
                                        ? t(
                                              'admin:menu.tools.menu_item.manage_admin.description_v1',
                                          )
                                        : t('admin:menu.tools.menu_item.manage_admin.description')}
                                </Description>
                            </MenuItem>
                        }
                        disabled={!canSeeAccounts}
                    >
                        {t('admin:admin_permissions_tooltip.hover.insufficient_permissions')}
                    </ConditionalPermissionsTooltip>

                    {showHrisConnectButton && (
                        <ConditionalPermissionsTooltip
                            trigger={
                                <MenuItem
                                    onSelect={() => {
                                        handleOnClick(
                                            '/admin/hris-sync',
                                            'admin:menu.tools.menu_item.hris_sync.label',
                                        );
                                    }}
                                    disabled={!canSeeHris}
                                >
                                    <Label bold>
                                        {t('admin:menu.tools.menu_item.hris_sync.label')}
                                    </Label>
                                    <Description>
                                        {t('admin:menu.tools.menu_item.hris_sync.description')}
                                    </Description>
                                </MenuItem>
                            }
                            disabled={!canSeeHris}
                        >
                            {t('admin:admin_permissions_tooltip.hover.insufficient_permissions')}
                        </ConditionalPermissionsTooltip>
                    )}
                </MenuList>
            </Menu>
        </Wrapper>
    );
};
